import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { mcGetTrending, mcGetNewPools } from '../lib/api';
import { Card, CardContent } from './ui/card';
import { Skeleton } from './ui/skeleton';
import { Flame, Sparkles, ArrowUpRight, Droplets, Clock } from 'lucide-react';
import { fmtUsd, fmtPrice } from '../lib/numfmt';

const TABS = [
  { key: 'trending', label: 'Trending', icon: Flame },
  { key: 'new', label: 'New pools', icon: Sparkles },
];

function timeAgo(iso) {
  if (!iso) return null;
  const s = (Date.now() - new Date(iso).getTime()) / 1000;
  if (!isFinite(s) || s < 0) return null;
  if (s < 3600) return `${Math.max(1, Math.round(s / 60))}m`;
  if (s < 86400) return `${Math.round(s / 3600)}h`;
  return `${Math.round(s / 86400)}d`;
}

function PoolRow({ chain, p, showAge, onOpen }) {
  const chg = parseFloat(p.change_h24);
  const up = isFinite(chg) && chg >= 0;
  return (
    <button
      type="button"
      data-testid={`pool-row-${p.pool_address}`}
      onClick={() => onOpen(p)}
      className="group w-full text-left border border-border bg-card hover:bg-accent hover:-translate-y-0.5 transition-[background-color,transform] duration-200 p-3"
    >
      <div className="flex items-center justify-between gap-2">
        <div className="min-w-0">
          <div className="flex items-center gap-1.5 font-bold text-sm truncate">
            <span className="truncate">{p.base_symbol}</span>
            <span className="text-muted-foreground font-normal">/ {p.quote_symbol || '—'}</span>
            <ArrowUpRight className="w-3.5 h-3.5 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity shrink-0" />
          </div>
          <div className="text-[10px] text-muted-foreground uppercase tracking-wide mt-0.5 flex items-center gap-1.5">
            <span className="truncate">{p.dex || 'dex'}</span>
            {showAge && timeAgo(p.created_at) && (
              <span className="flex items-center gap-0.5 text-primary"><Clock className="w-2.5 h-2.5" />{timeAgo(p.created_at)}</span>
            )}
          </div>
        </div>
        <div className={`text-sm font-mono font-semibold shrink-0 ${isFinite(chg) ? (up ? 'text-emerald-500' : 'text-red-500') : 'text-muted-foreground'}`}>
          {isFinite(chg) ? `${up ? '+' : ''}${chg.toFixed(1)}%` : '—'}
        </div>
      </div>
      <div className="grid grid-cols-3 gap-2 mt-2.5 text-xs font-mono">
        <div>
          <div className="text-[9px] text-muted-foreground uppercase tracking-wide">Price</div>
          {fmtPrice(p.price_usd)}
        </div>
        <div>
          <div className="text-[9px] text-muted-foreground uppercase tracking-wide">24h Vol</div>
          {fmtUsd(p.volume_24h)}
        </div>
        <div>
          <div className="text-[9px] text-muted-foreground uppercase tracking-wide flex items-center gap-0.5"><Droplets className="w-2.5 h-2.5" />Liq</div>
          {fmtUsd(p.liquidity_usd)}
        </div>
      </div>
    </button>
  );
}

/**
 * Live "what's hot / what's fresh" board for a chain, from GeckoTerminal.
 * Renders on the Explorer home for any GT-covered (non-Krown) chain.
 */
export default function McTrendingPools({ chain, chainLabel }) {
  const navigate = useNavigate();
  const [tab, setTab] = useState('trending');
  const [data, setData] = useState({ trending: null, new: null });
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    const [t, n] = await Promise.allSettled([mcGetTrending(chain), mcGetNewPools(chain)]);
    setData({
      trending: t.status === 'fulfilled' ? t.value : { pools: [], unsupported: true },
      new: n.status === 'fulfilled' ? n.value : { pools: [], unsupported: true },
    });
    setLoading(false);
  }, [chain]);

  useEffect(() => { load(); }, [load]);

  const openPool = (p) => {
    if (p.base_address) navigate(`/explorer/${chain}/token/${p.base_address}?pool=${p.pool_address}`);
  };

  const active = data[tab];
  const pools = active?.pools || [];

  return (
    <section data-testid="home-trending">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <h2 className="mono-label">Discover on {chainLabel || chain}</h2>
        <div className="flex rounded-full border border-border overflow-hidden">
          {TABS.map((t) => (
            <button
              key={t.key}
              data-testid={`trending-tab-${t.key}`}
              onClick={() => setTab(t.key)}
              className={`px-3.5 py-1.5 text-xs font-medium transition-colors flex items-center gap-1.5 ${tab === t.key ? 'bg-primary text-primary-foreground' : 'bg-card text-muted-foreground hover:text-foreground'}`}
            >
              <t.icon className="w-3.5 h-3.5" /> {t.label}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="trending-loading">
          {Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} className="h-28" />)}
        </div>
      ) : active?.unsupported ? (
        <Card className="border-dashed" data-testid="trending-unsupported">
          <CardContent className="pt-6 text-sm text-muted-foreground">
            {active.reason || `Trending pools aren't available for ${chainLabel || chain} yet.`}
          </CardContent>
        </Card>
      ) : pools.length === 0 ? (
        <Card className="border-dashed" data-testid="trending-empty">
          <CardContent className="pt-6 text-sm text-muted-foreground">
            No {tab === 'new' ? 'new pools' : 'trending pools'} found right now — try again shortly.
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="trending-grid">
          {pools.map((p) => (
            <PoolRow key={p.pool_address} chain={chain} p={p} showAge={tab === 'new'} onOpen={openPool} />
          ))}
        </div>
      )}
    </section>
  );
}
